import React, { useCallback, useEffect, useRef, useMemo, useState } from "react";

import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    
    Paper,
    Snackbar,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";

import {
    Add,
    Delete,
    Edit,
    Refresh,
    Search,
} from "@mui/icons-material";

import {
    createAccountOffice,
    deleteAccountOffice,
    getAccountOfficeList,
    getAccountOfficeNextSrNo,
    updateAccountOffice,
} from "../../../services/accountOfficeService";

import { currentMonth } from "../../../services/dashBoardService";
import PageHeader from "../../../utils/PageHeader";


import AccessTimeIcon from "@mui/icons-material/AccessTime";


// ======================================================
// INITIAL FORM
// ======================================================
const emptyForm = {
    srno: "",
    ShopCode: "",
    WorkOrder: "",
    Amount: 0,
   
};


// ======================================================
// COMPONENT
// ======================================================
const AccountOffice = () => {

    const [monthData, setMonthData] = useState(null);

    const [form, setForm] = useState(emptyForm);

    const [rows, setRows] = useState([]);

    const [loading, setLoading] = useState(false);

    const [saving, setSaving] = useState(false);

    const [editMode, setEditMode] = useState(false);

    const [search, setSearch] = useState("");

    const [searchInput, setSearchInput] = useState("");

    const [page, setPage] = useState(0);

    const [rowsPerPage, setRowsPerPage] = useState(25);

    const [totalRows, setTotalRows] = useState(0);

    const [deleteDialog, setDeleteDialog] = useState(false);

    const [selectedRow, setSelectedRow] = useState(null);

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });



    const shopRef = useRef(null);
    const workOrderRef = useRef(null);
    const amountRef = useRef(null);
    const saveRef = useRef(null);



    const handleEnterNext = (event, nextRef, submit = false) => {
        if (event.key !== "Enter") return;

        event.preventDefault();
        event.stopPropagation();

        if (submit) {
            saveRef.current?.click();
            return;
        }

        nextRef?.current?.focus();
    };






    // ====================================================
    // CURRENT MONTH
    // ====================================================
    const loadCurrentMonth = async () => {

        try {

            const res = await currentMonth();

            setMonthData(res.data.data);

            //  console.log({"Month " : monthData.month});

        } catch (err) {

            console.error(
                "Current month error:",
                err
            );

        }
    };


    // ====================================================
    // MONTH/YEAR
    // ====================================================
    const currentMonthNumber = useMemo(() => {

        if (monthData?.month) {
            return Number(monthData?.month);
        }

        return new Date().getMonth() + 1;

    }, [monthData?.month]);


    const currentYearNumber = useMemo(() => {

        if (monthData?.year) {
            return Number(monthData?.year);
        }

        return new Date().getFullYear();

    }, [monthData?.year]);


    // ====================================================
    // MONTH NAME
    // ====================================================
    const monthName = useMemo(() => {

        return new Date(
            currentYearNumber,
            currentMonthNumber - 1,
            1
        ).toLocaleString(
            "en-US",
            {
                month: "long",
            }
        );

    }, [
        currentMonthNumber,
        currentYearNumber,
    ]);


    // ====================================================
    // LOAD NEXT SRNO
    // ====================================================
    const loadNextSrNo = useCallback(async () => {
        try {
            const res = await getAccountOfficeNextSrNo();

            setForm((prev) => ({
                ...prev,
                srno: res.srno,
            }));

            setTimeout(() => {
                shopRef.current?.focus();
                shopRef.current?.select?.();
            }, 50);

        } catch (err) {
            console.error("Next srno error:", err);
        }
    }, []);


    // ====================================================
    // LOAD LIST
    // ====================================================
    const loadList = useCallback(async () => {

        setLoading(true);

        try {

            const res = await getAccountOfficeList({
                page: page + 1,
                limit: rowsPerPage,
                search,
                month: currentMonthNumber,
                year: currentYearNumber,
            });

            setRows(res.data || []);

//console.log("Account Office list:", res.data);

            setTotalRows(
                Number(res.total || 0)
            );

        } catch (err) {

            console.error(
                "Account Office list error:",
                err
            );

            showMessage(
                "Unable to load data",
                "error"
            );

        } finally {

            setLoading(false);

        }

    }, [
        page,
        rowsPerPage,
        search,
        currentMonthNumber,
        currentYearNumber,
    ]);


    // ====================================================
    // INITIAL LOAD
    // ====================================================
    useEffect(() => {

        loadCurrentMonth();

    }, []);


    // ====================================================
    // LOAD LIST AFTER MONTH AVAILABLE
    // ====================================================
    useEffect(() => {

        if (
            currentMonthNumber &&
            currentYearNumber
        ) {

            loadList();

        }

    }, [
        loadList,
        currentMonthNumber,
        currentYearNumber,
    ]);


    // ====================================================
    // RESET FORM
    // ====================================================
    const resetForm = async (focusTicket = true) => {
        setEditMode(false);

        setForm({
            ...emptyForm,
            employeeType: form.employeeType,
            employeeCategory: form.employeeCategory,
        });

        await loadNextSrNo();

        if (focusTicket) {
            setTimeout(() => {
                shopRef.current?.focus();
                shopRef.current?.select?.();
            }, 100);
        }
    };


    // ====================================================
    // FORM CHANGE
    // ====================================================
    const handleChange = (field) => (event) => {

        setForm((prev) => ({
            ...prev,
            [field]: event.target.value,
        }));

    };


    // ====================================================
    // SAVE / UPDATE
    // ====================================================
    const handleSubmit = async (event) => {

        event.preventDefault();

       
        if (!form.ShopCode) {
            showMessage(
                "Please enter Shop Code",
                "error"
            );
            return;
        }

        if (!form.WorkOrder) {
            showMessage(
                "Please enter Work Order",
                "error"
            );
            return;
        }

      

        if (
            Number(form.Amount) <=0 
        ) {
            showMessage(
                "Amount Must be more then Zero",
                "error"
            );
            return;
        }

        setSaving(true);

        try {

            const payload = {
                ShopCode: form.ShopCode,
                WorkOrder: form.WorkOrder,
               Amount: Number(form.Amount) || 0,
               
              
            };

            if (
                monthData?.month != null &&
                monthData?.year != null
            ) {
                payload.month = monthData.month;
                payload.year = monthData.year;
            }


            if (editMode) {
                await updateAccountOffice(
                    form.srno,
                    payload
                );

                showMessage(
                    "Entry updated successfully",
                    "success"
                );

                setEditMode(false);

                await loadList();

                await resetForm();

            } else {



                await createAccountOffice(payload);

                showMessage(
                    "Entry saved successfully",
                    "success"
                );

                // Prepare next entry
                setForm({
                    ...emptyForm,
                   
                });

                // Get next serial number
                const next = await getAccountOfficeNextSrNo();

                setForm((prev) => ({
                    ...prev,
                    srno: next.srno,
                }));

                // Focus Ticket Number for next entry
                setTimeout(() => {
                    shopRef.current?.focus();
                    shopRef.current?.select?.();
                }, 100);
            }


            // ================================================
            // IMPORTANT:
            // Immediately refresh after add/update
            // ================================================
            setPage(0);

            await loadList();

            await resetForm();

        } catch (err) {

            console.error(
                "Save Incentive error:",
                err
            );

            showMessage(
                err?.response?.data?.message ||
                "Unable to save entry",
                "error"
            );

        } finally {

            setSaving(false);
            shopRef.current?.focus();
            shopRef.current?.select?.();
        }

    };


    // ====================================================
    // EDIT
    // ====================================================
   // ======================================================
// EDIT
// ======================================================
const handleEdit = (row) => {
    setEditMode(true);

    setForm({
        ...emptyForm,

        srno: row.srno ?? "",

        ShopCode: row.ShopCode ?? "",

        WorkOrder: row.WorkOrder ?? "",

        // API returns amount as CTMEAMT2
        Amount: row.CTMEAMT2 ?? row.Amount ?? 0,
    });

    window.scrollTo({
        top: 0,
        behavior: "smooth",
    });

    setTimeout(() => {
        shopRef.current?.focus();
        shopRef.current?.select?.();
    }, 300);
};

    // ====================================================
    // DELETE CONFIRM
    // ====================================================
    const openDeleteDialog = (row) => {

        setSelectedRow(row);

        setDeleteDialog(true);

    };


    // ====================================================
    // DELETE
    // ====================================================
    const handleDelete = async () => {

    if (!selectedRow) {
        return;
    }

    if (!monthData?.month || !monthData?.year) {
        showMessage(
            "Month and year are required",
            "error"
        );
        return;
    }

    try {

        await deleteAccountOffice(
            selectedRow.srno,
            {
                month: monthData.month,
                year: monthData.year
            }
        );

        showMessage(
            "Entry deleted successfully",
            "success"
        );

        setDeleteDialog(false);
        setSelectedRow(null);

        await loadList();
        await loadNextSrNo();

    } catch (err) {

        console.error(
            "Delete error:",
            err
        );

        showMessage(
            err?.response?.data?.message ||
            "Unable to delete entry",
            "error"
        );
    }
};


    // ====================================================
    // SEARCH
    // ====================================================
    const handleSearch = () => {

        setPage(0);

        setSearch(
            searchInput.trim()
        );

    };


    // ====================================================
    // CLEAR SEARCH
    // ====================================================
    const handleClearSearch = async () => {

        setSearchInput("");

        setSearch("");

        setPage(0);

    };


    // ====================================================
    // REFRESH
    // ====================================================
    const handleRefresh = async () => {

        setSearchInput("");

        setSearch("");

        setPage(0);

        await loadList();

        await loadNextSrNo();

    };


    // ====================================================
    // MESSAGE
    // ====================================================
    const showMessage = (
        message,
        severity = "success"
    ) => {

        setSnackbar({
            open: true,
            message,
            severity,
        });

    };


    // ====================================================
    // CATEGORY DISPLAY
    // ====================================================
   

    return (
        <Box
            sx={{
                p: {
                    xs: 1,
                    sm: 2,
                    md: 3,
                },
            }}
        >

            {/* =================================================
          HEADER
      ================================================= */}
           

<PageHeader
        title="Insentive Data Entry"
        subtitle={`Data entry status for the month of: ${monthName} - ${currentYearNumber}`}
        icon={AccessTimeIcon}
       // statusLabel={'Records: ' + totalRows}
       //  statusValue="CSV File"
        statusChip={{
          label: loading
            ? "Loading..."
            : totalRows > 0
              ? "Data Found"
              : "No Data",
          color: loading ? "warning" : totalRows > 0 ? "primary" : "default",
        }}
        showRefresh
        onRefresh={handleRefresh}
        // refreshing={loading}
      />

            {/* =================================================
          ENTRY FORM
      ================================================= */}
            <Card
                elevation={2}
                sx={{
                    mb: 2,
                    borderRadius: 2,
                }}
            >

                <CardContent>

                    <Box
                        component="form"
                        onSubmit={handleSubmit}
                    >

                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: {
                                    xs: "1fr",
                                    md: "1fr 1fr",
                                },
                                gap: 2,
                            }}
                        >

                            {/* ========================================
                  LEFT
              ======================================== */}
                            <Stack spacing={2}>

                                <TextField
                                    label="Sr No"
                                    value={form.srno}
                                    size="small"
                                    fullWidth
                                    disabled
                                />

                               
                                <TextField
                                    inputRef={workOrderRef}
                                    label="Work Order"
                                    value={form.WorkOrder}
                                    onChange={(e) => {
                                        const value = e.target.value;

                                        if (/^\d*$/.test(value)) {
                                            setForm((prev) => ({
                                                ...prev,
                                                WorkOrder: value,
                                            }));
                                        }
                                    }}
                                    onKeyDown={(e) =>
                                        handleEnterNext(e, amountRef)
                                    }
                                    size="small"
                                    fullWidth
                                    required
                                    autoComplete="off"
                                    slotProps={{
                                        htmlInput: {
                                            inputMode: "numeric",
                                            pattern: "[0-9]*",
                                            maxLength: 8,
                                        },
                                    }}
                                />

                               

                            </Stack>


                            {/* ========================================
                  RIGHT
              ======================================== */}
                            <Stack spacing={2}>

                               
                                <TextField
                                    inputRef={shopRef}
                                    label="Shop Code"
                                    type="number"
                                    value={form.ShopCode}
                                    onChange={(e) => {
                                        const value = e.target.value;

                                        // Allow only numeric value
                                        if (/^\d*$/.test(value)) {
                                            setForm((prev) => ({
                                                ...prev,
                                                ShopCode: value,
                                            }));
                                        }
                                    }}
                                    onKeyDown={(e) =>
                                        handleEnterNext(e, workOrderRef)
                                    }
                                    size="small"
                                    fullWidth
                                    required
                                    autoComplete="off"
                                    slotProps={{
                                        htmlInput: {
                                            inputMode: "numeric",
                                            pattern: "[0-9]*",
                                            maxLength: 3,
                                        },
                                    }}
                                />


                               <TextField
                                    inputRef={amountRef}
                                    label="Amount"
                                    type="number"
                                    value={form.Amount}
                                    onChange={handleChange("Amount")}
                                    onKeyDown={(e) =>
                                        handleEnterNext(e, saveRef)
                                    }
                                    size="small"
                                    fullWidth
                                    inputProps={{
                                        min: 0,
                                    }}
                                    autoComplete="off"
                                />

                              

                            </Stack>

                        </Box>


                        {/* ==========================================
                BUTTONS
            ========================================== */}
                        <Stack
                            direction={{
                                xs: "column",
                                sm: "row",
                            }}
                            spacing={2}
                            justifyContent="center"
                            sx={{
                                mt: 3,
                            }}
                        >

                            <Button
                                ref={saveRef}
                                type="submit"
                                variant="contained"
                                disabled={saving}
                                startIcon={
                                    saving ? (
                                        <CircularProgress
                                            size={18}
                                            color="inherit"
                                        />
                                    ) : editMode ? (
                                        <Edit />
                                    ) : (
                                        <Add />
                                    )
                                }
                                sx={{
                                    minWidth: 160,
                                    bgcolor: "#fff500",
                                    color: "#000",
                                    fontWeight: 700,
                                    "&:hover": {
                                        bgcolor: "#e6da00",
                                    },
                                }}
                            >
                                {saving
                                    ? "Saving..."
                                    : editMode
                                        ? "Update"
                                        : "Save"}
                            </Button>


                            <Button
                                variant="contained"
                                color="error"
                                onClick={resetForm}
                                sx={{
                                    minWidth: 160,
                                    fontWeight: 700,
                                }}
                            >
                                {editMode
                                    ? "Cancel Edit"
                                    : "Clear"}
                            </Button>

                        </Stack>

                    </Box>

                </CardContent>

            </Card>


            {/* =================================================
          LIST
      ================================================= */}
            <Card
                elevation={2}
                sx={{
                    borderRadius: 2,
                }}
            >

                <CardContent>

                    {/* ============================================
              SEARCH TOOLBAR
          ============================================ */}
                    <Stack
                        direction={{
                            xs: "column",
                            md: "row",
                        }}
                        spacing={1}
                        sx={{
                            mb: 2,
                        }}
                    >

                        <TextField
                            label="Search"
                            placeholder="Sr No / Shop / Work Order /user"
                            size="small"
                            value={searchInput}
                            onChange={(e) =>
                                setSearchInput(
                                    e.target.value
                                )
                            }
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    handleSearch();
                                }
                            }}
                            sx={{
                                flex: 1,
                            }}
                        />

                        <Button
                            variant="contained"
                            startIcon={<Search />}
                            onClick={handleSearch}
                        >
                            Search
                        </Button>

                        <Button
                            variant="outlined"
                            onClick={handleClearSearch}
                        >
                            Clear
                        </Button>

                        <IconButton
                            onClick={handleRefresh}
                            color="primary"
                            title="Refresh"
                        >
                            <Refresh />
                        </IconButton>

                    </Stack>


                    {/* ============================================
              ROW COUNT
          ============================================ */}
                    <Typography
                        sx={{
                            mb: 1,
                            fontWeight: 700,
                        }}
                    >
                        No of Rows:{" "}
                        <Box
                            component="span"
                            sx={{
                                color: "error.main",
                                ml: 1,
                            }}
                        >
                            {totalRows}
                        </Box>
                    </Typography>


                    {/* ============================================
              TABLE
          ============================================ */}
                    <TableContainer
                        component={Paper}
                        sx={{
                            maxHeight: 550,
                        }}
                    >

                        <Table
                            stickyHeader
                            size="small"
                        >

                            <TableHead>

                                <TableRow>

                                    <TableCell>
                                        Sr No
                                    </TableCell>

                                    <TableCell>
                                        Shop Code
                                    </TableCell>

                                  
                                    <TableCell>
                                        Work Order
                                    </TableCell>

                                    <TableCell align="right">
                                        Amount
                                    </TableCell>


                                    <TableCell align="right">
                                        User
                                    </TableCell>

                                
                                    <TableCell align="center">
                                        Action
                                    </TableCell>

                                </TableRow>

                            </TableHead>


                            <TableBody>

                                {loading ? (

                                    <TableRow>

                                        <TableCell
                                            colSpan={9}
                                            align="center"
                                            sx={{
                                                py: 5,
                                            }}
                                        >

                                            <CircularProgress />

                                        </TableCell>

                                    </TableRow>

                                ) : rows.length === 0 ? (

                                    <TableRow>

                                        <TableCell
                                            colSpan={9}
                                            align="center"
                                        >
                                            No data found
                                        </TableCell>

                                    </TableRow>

                                ) : (

                                    rows.map((row) => (

                                        <TableRow
                                            key={row.srno}
                                            hover
                                        >

                                            <TableCell>
                                                {row.srno}
                                            </TableCell>

                                            <TableCell>
                                                {row.ShopCode}
                                            </TableCell>

                                                                                    
                                            <TableCell>
                                                {row.WorkOrder}
                                            </TableCell>

                                            <TableCell align="right">
                                                {row.CTMEAMT2}
                                            </TableCell>

                                           <TableCell align="right">
                                                {row.uname}
                                            </TableCell>

                                            <TableCell align="center">

                                                <IconButton
                                                    color="primary"
                                                    onClick={() =>
                                                        handleEdit(row)
                                                    }
                                                    title="Edit"
                                                    size="small"
                                                >
                                                    <Edit />
                                                </IconButton>


                                                <IconButton
                                                    color="error"
                                                    onClick={() =>
                                                        openDeleteDialog(
                                                            row
                                                        )
                                                    }
                                                    title="Delete"
                                                    size="small"
                                                >
                                                    <Delete />
                                                </IconButton>

                                            </TableCell>

                                        </TableRow>

                                    ))

                                )}

                            </TableBody>

                        </Table>

                    </TableContainer>


                    {/* ============================================
              PAGINATION
          ============================================ */}
                    <TablePagination
                        component="div"
                        count={totalRows}
                        page={page}
                        onPageChange={(
                            _event,
                            newPage
                        ) => {
                            setPage(newPage);
                        }}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(event) => {

                            setRowsPerPage(
                                Number(event.target.value)
                            );

                            setPage(0);

                        }}
                        rowsPerPageOptions={[
                            10,
                            25,
                            50,
                            100,
                        ]}
                    />

                </CardContent>

            </Card>


            {/* =================================================
          DELETE DIALOG
      ================================================= */}
            <Dialog
                open={deleteDialog}
                onClose={() =>
                    setDeleteDialog(false)
                }
            >

                <DialogTitle>
                    Delete Entry
                </DialogTitle>

                <DialogContent>

                    <Typography>
                        Are you sure you want to delete
                        Sr No{" "}
                        <strong>
                            {selectedRow?.srno}
                        </strong>
                        ?
                    </Typography>

                </DialogContent>

                <DialogActions>

                    <Button
                        onClick={() =>
                            setDeleteDialog(false)
                        }
                    >
                        Cancel
                    </Button>

                    <Button
                        color="error"
                        variant="contained"
                        onClick={handleDelete}
                    >
                        Delete
                    </Button>

                </DialogActions>

            </Dialog>


            {/* =================================================
          SNACKBAR
      ================================================= */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() =>
                    setSnackbar((prev) => ({
                        ...prev,
                        open: false,
                    }))
                }
            >

                <Alert
                    severity={snackbar.severity}
                    variant="filled"
                    onClose={() =>
                        setSnackbar((prev) => ({
                            ...prev,
                            open: false,
                        }))
                    }
                >
                    {snackbar.message}
                </Alert>

            </Snackbar>

        </Box>
    );
};





export default AccountOffice;